import { fillCopy } from './request-access-copy.js'

/**
 * Conflict notice strings (GH #1912 option B, docs/ux/2026-10-24-conflict-notice-ux.md, Strings; approved by the
 * user). The other editor is never named: "Someone" is the only form. Copy changes go back to the user.
 */
export const conflictNoticeCopy = {
  /** 1 */
  leadOne: 'Someone changed {field} while you were editing.',
  /** 2 */
  leadMany: 'Someone changed these while you were editing:',
  /** 4 */
  more: 'and {n} more',
  /** 7 */
  subline: "Your changes aren't saved yet.",
  /** 8 */
  keep: 'Keep my changes',
  /** 9. Not `useTheirs`: the React Compiler reads a `use…` member as a hook */
  takeTheirs: 'Use their version',
  /** 10, announced as a status */
  kept: 'Your changes are saved.',
  /** 11 */
  switched: 'Switched to their version.',
  undo: 'Undo',
  /** 12, several fields; one field uses string 1 */
  offscreenMany: 'Someone changed {count} things while you were editing.',
  review: 'Review',
  /** 13 */
  keepFailed: "Couldn't save your changes.",
  tryAgain: 'Try again',
  /** 14 */
  region: 'Editing conflict',
} as const

interface FieldLabel {
  /** The field mid-sentence (string 1) */
  mid: string
  /** The field as a list item, capitalised, without "the" (string 3) */
  item: string
}

/** String 15 */
const fieldLabels: Record<string, FieldLabel> = {
  pollOptions: { mid: 'the poll options', item: 'Poll options' },
  pollGroups: { mid: 'the option groups', item: 'Option groups' },
  finalOption: { mid: 'the final choice', item: 'Final choice' },
  invited: { mid: "who's invited", item: "Who's invited" },
  tags: { mid: 'the tags', item: 'Tags' },
  instruments: { mid: 'the instruments', item: 'Instruments' },
  todos: { mid: 'the to-dos', item: 'To-dos' },
  writers: { mid: 'the writers', item: 'Writers' },
  tracks: { mid: 'the tracks', item: 'Tracks' },
  events: { mid: 'the timeline events', item: 'Timeline events' },
  mediaIds: { mid: 'the playlist', item: 'Playlist items' },
  eventCommands: { mid: 'the event actions', item: 'Event actions' },
}

/** Any other structured field */
const otherField = { mid: 'a setting', item: 'Another setting', items: 'Other settings' }

/** At most this many labels in the list, then "and {n} more" (Layout) */
const maxListed = 5

/** The labels the banner lists for several fields: fields with no label of their own share one item */
const listedItems = (fields: readonly string[]) => {
  const known = fields.flatMap(field => fieldLabels[field]?.item ?? [])
  const others = fields.length - known.length
  return others === 0 ? known : [...known, others === 1 ? otherField.item : otherField.items]
}

export type ConflictLead =
  { kind: 'one'; text: string } | { kind: 'many'; lead: string; items: string[]; more: string | null }

/** The banner's lead: one sentence for one field, or the lead line and a list of labels for several */
export const conflictLead = (fields: readonly string[]): ConflictLead => {
  if (fields.length === 1) {
    const mid = fieldLabels[fields[0] ?? '']?.mid ?? otherField.mid
    return { kind: 'one', text: fillCopy(conflictNoticeCopy.leadOne, { field: mid }) }
  }
  const items = listedItems(fields)
  const hidden = items.length - maxListed
  return {
    kind: 'many',
    lead: conflictNoticeCopy.leadMany,
    items: items.slice(0, maxListed),
    more: hidden > 0 ? fillCopy(conflictNoticeCopy.more, { n: hidden }) : null,
  }
}

/** String 12: the snackbar when the panel that edits the fields is off screen */
export const conflictOffscreenMessage = (fields: readonly string[]) => {
  const lead = conflictLead(fields)
  if (lead.kind === 'one') {
    return lead.text
  }
  /**
   * GH #2713: count what the banner lists, so "3 things" never meets a list of two. When it lists only "Other
   * settings", that one item stands for several, and the count says how many.
   */
  const listed = listedItems(fields).length
  return fillCopy(conflictNoticeCopy.offscreenMany, { count: listed > 1 ? listed : fields.length })
}
